import { Check, ChevronDown, Columns3, Filter, LayoutGrid, List } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchInput } from '~/components/common/search-input';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import type {
  ProductFilterState,
  ProductViewDensity,
  ProductViewMode,
} from '~/features/explorer/types/products-catalog.types';
import { SORT_OPTIONS } from '~/features/explorer/utils/products-catalog';
import { cn } from '~/utils/cn';

import { ProductDensityControl } from './product-density-control';
import { ProductFilterDrawer } from './product-filter-drawer';

/** Column visibility map — a column is visible unless explicitly set to `false`. */
export type ColumnVisibility = Record<string, boolean>;

/** A toggleable table column shown in the column-visibility popover. */
export interface ProductColumnOption {
  id: string;
  label: string;
}

/** Sort dropdown option. */
export interface ProductSortOption {
  value: string;
  label: string;
}

interface ProductFilterBarProps {
  filters: ProductFilterState;
  onChange: (filters: ProductFilterState) => void;
  vendorOptions: string[];
  viewMode: ProductViewMode;
  onViewModeChange: (mode: ProductViewMode) => void;
  disabled?: boolean;
  totalCount: number;
  tableColumns?: ProductColumnOption[];
  columnVisibility?: ColumnVisibility;
  onColumnVisibilityChange?: (visibility: ColumnVisibility) => void;
  /** Sort dropdown options. Defaults to the shared `SORT_OPTIONS`. */
  sortOptions?: readonly ProductSortOption[];
  /** When true, the sort dropdown is hidden in table view (sorting moves to the
   *  column headers). Card view still shows the dropdown. Default: false. */
  hideSortInTableView?: boolean;
  /** Column-picker layout: `'pills'` (default) or a `'checkbox'` list. */
  columnPickerVariant?: 'pills' | 'checkbox';
  /** Opt-in: replaces the built-in Filter button + hardcoded drawer with a
   *  caller-supplied filter control (e.g. a dynamic server-driven drawer).
   *  When set, `vendorOptions`/`onChange`-filter fields are not used here. */
  filterSlot?: ReactNode;
  /** Hide the Filter button + drawer entirely — used by the sidebar layout,
   *  where a left facet panel owns filtering instead of the toolbar. */
  hideFilterButton?: boolean;
  /** Optional control rendered just before the search box (e.g. the catalog
   *  search field picker). */
  searchFieldSlot?: ReactNode;
  /** Overrides the default search placeholder (e.g. to name the picked field). */
  searchPlaceholder?: string;
  /** Current grid-density preset. When provided (with `onViewDensityChange`) a
   *  density switch renders next to the view toggle (card view only). */
  viewDensity?: ProductViewDensity;
  onViewDensityChange?: (density: ProductViewDensity) => void;
}

/** Column-visibility popover (table view only). */
function ColumnsPopover({
  tableColumns,
  columnVisibility,
  onColumnVisibilityChange,
  variant = 'pills',
}: {
  tableColumns: ProductColumnOption[];
  columnVisibility: ColumnVisibility;
  onColumnVisibilityChange?: (visibility: ColumnVisibility) => void;
  variant?: 'pills' | 'checkbox';
}) {
  const { t } = useTranslation('explorer');
  const [open, setOpen] = useState(false);
  const anyHidden = Object.values(columnVisibility).some((v) => !v);
  const visibleCount = tableColumns.filter((c) => columnVisibility[c.id] !== false).length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn('h-10 cursor-pointer gap-1.5', anyHidden && 'border-primary text-primary')}
        >
          <Columns3 className="h-4 w-4 shrink-0" />
          {t('products.columns')}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="border-border/60 w-72 overflow-hidden rounded-2xl p-0 shadow-lg"
      >
        <div className="bg-muted/30 flex items-center justify-between border-b px-4 py-3">
          <div>
            <p className="text-sm font-semibold">{t('products.toggleColumns')}</p>
            <p className="text-muted-foreground mt-0.5 text-[11px]">
              {t('products.columnsVisible', { visible: visibleCount, total: tableColumns.length })}
            </p>
          </div>
          <Button
            type="button"
            variant="link"
            onClick={() => {
              const all: ColumnVisibility = {};
              tableColumns.forEach((c) => {
                all[c.id] = true;
              });
              onColumnVisibilityChange?.(all);
            }}
            className="text-primary h-auto cursor-pointer p-0 text-[11px] font-medium"
          >
            {t('products.showAll')}
          </Button>
        </div>
        {variant === 'checkbox' ? (
          <div className="max-h-[320px] overflow-y-auto p-2">
            {tableColumns.map((col) => {
              const isVisible = columnVisibility[col.id] !== false;
              return (
                <label
                  key={col.id}
                  className="hover:bg-muted/50 flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5"
                >
                  <Checkbox
                    checked={isVisible}
                    onCheckedChange={() => {
                      onColumnVisibilityChange?.({ ...columnVisibility, [col.id]: !isVisible });
                    }}
                  />
                  <span className="text-sm">{col.label}</span>
                </label>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 p-4">
            {tableColumns.map((col) => {
              const isVisible = columnVisibility[col.id] !== false;
              return (
                <Button
                  key={col.id}
                  type="button"
                  variant="outline"
                  onClick={() => {
                    onColumnVisibilityChange?.({ ...columnVisibility, [col.id]: !isVisible });
                  }}
                  className={cn(
                    'h-auto cursor-pointer gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium',
                    isVisible
                      ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground border-border',
                  )}
                >
                  {isVisible && <Check className="h-3 w-3 shrink-0" />}
                  {col.label}
                </Button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Products toolbar in the app's filter language: live search, a Filter button
 * that opens the drawer (grade / source status / critical / vendors) with an
 * active-count badge, an optional column toggle (table view), server sort, and
 * a card/table view switch.
 */
export function ProductFilterBar({
  filters,
  onChange,
  vendorOptions,
  viewMode,
  onViewModeChange,
  disabled = false,
  totalCount,
  tableColumns,
  columnVisibility = {},
  onColumnVisibilityChange,
  sortOptions,
  hideSortInTableView = false,
  columnPickerVariant = 'pills',
  filterSlot,
  hideFilterButton = false,
  searchFieldSlot,
  searchPlaceholder,
  viewDensity,
  onViewDensityChange,
}: ProductFilterBarProps) {
  const { t } = useTranslation('explorer');
  const [filterOpen, setFilterOpen] = useState(false);

  const resolvedSortOptions = sortOptions ?? SORT_OPTIONS;
  // Card view always shows the dropdown; in table view it's hidden when sorting
  // has moved to the column headers.
  const showSortDropdown = !(hideSortInTableView && viewMode === 'table');

  const activeFilterCount =
    (filters.grade ? 1 : 0) +
    (filters.sourceStatus ? 1 : 0) +
    (filters.hasCriticalIssues ? 1 : 0) +
    filters.vendors.length;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {searchFieldSlot}
      <SearchInput
        value={filters.search}
        onChange={(v) => {
          onChange({ ...filters, search: v });
        }}
        placeholder={searchPlaceholder ?? t('products.searchPlaceholder')}
        disabled={disabled}
        className="min-w-[220px] flex-1"
        inputClassName="h-10"
      />

      {/* Filter control — hidden when a left sidebar owns filtering; otherwise a
          caller-supplied slot (dynamic server filters) or the built-in drawer. */}
      {!hideFilterButton &&
        (filterSlot ?? (
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={() => {
              setFilterOpen(true);
            }}
            className={cn(
              'h-10 gap-1.5',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              activeFilterCount > 0 && 'border-primary text-primary',
            )}
          >
            <Filter className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('products.filter')}
            {activeFilterCount > 0 && (
              <span className="bg-primary inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold text-white tabular-nums">
                {activeFilterCount}
              </span>
            )}
          </Button>
        ))}

      {viewMode === 'table' && tableColumns && tableColumns.length > 0 && (
        <ColumnsPopover
          tableColumns={tableColumns}
          columnVisibility={columnVisibility}
          variant={columnPickerVariant}
          {...(onColumnVisibilityChange ? { onColumnVisibilityChange } : {})}
        />
      )}

      {/* Sort — dropdown in card view (and in table view unless header-sort is on) */}
      {showSortDropdown && (
        <Select
          value={filters.sortBy}
          onValueChange={(v) => {
            onChange({ ...filters, sortBy: v });
          }}
          disabled={disabled}
        >
          <SelectTrigger className="h-10 w-auto cursor-pointer gap-1 text-sm font-medium [&>svg]:opacity-50">
            <span className="text-muted-foreground mr-0.5 font-normal">{t('products.sort')}</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end" className="border-border/60 rounded-xl shadow-lg">
            {resolvedSortOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="cursor-pointer">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Grid-density switch — card view only */}
      {viewMode === 'grid' && viewDensity && onViewDensityChange && (
        <ProductDensityControl value={viewDensity} onChange={onViewDensityChange} />
      )}

      {/* Card / table toggle */}
      <SegmentedTabs<ProductViewMode>
        value={viewMode}
        onChange={onViewModeChange}
        ariaLabel={t('products.tabsAriaLabel')}
        size="icon"
        className="bg-muted h-10 w-auto shrink-0 border-0 sm:max-w-none"
        options={[
          {
            value: 'grid',
            label: <LayoutGrid className="h-4 w-4" aria-hidden="true" />,
            ariaLabel: t('products.cardView'),
          },
          {
            value: 'table',
            label: <List className="h-4 w-4" aria-hidden="true" />,
            ariaLabel: t('products.tableView'),
          },
        ]}
      />

      {totalCount > 0 && (
        <span className="text-muted-foreground ml-auto text-sm tabular-nums">
          <span className="text-foreground font-semibold">{totalCount.toLocaleString()}</span>{' '}
          {t('products.countSuffix')}
        </span>
      )}

      {!hideFilterButton && !filterSlot && (
        <ProductFilterDrawer
          open={filterOpen}
          onOpenChange={setFilterOpen}
          value={{
            grade: filters.grade,
            sourceStatus: filters.sourceStatus,
            hasCriticalIssues: filters.hasCriticalIssues,
            vendors: filters.vendors,
          }}
          vendorOptions={vendorOptions}
          onApply={(next) => {
            onChange({ ...filters, ...next });
          }}
        />
      )}
    </div>
  );
}
